import * as Popover from '@radix-ui/react-popover';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Language, Service } from '@yleisradio/areena-types';
import FavoriteIcon from 'assets/favoriteOffIcon.svg';
import LogOutIcon from 'assets/log-out.svg';
import BarChartIcon from 'assets/barChart.svg';
import ChevronRightIcon from 'assets/chevronRight.svg';
import MailIcon from 'assets/mail.svg';
import { useLogOutAction } from 'components/Controls/Control/actions/useLogOutAction';
import { Link } from 'components/Link';
import { UserInitials } from 'components/UserInitials/UserInitials';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { useTranslation } from 'hooks/useTranslation';
import { useUILanguage } from 'hooks/useUILanguage';
import { FC, useRef, useState } from 'react';
import {
  personalPodcastRoutes,
  personalRoutes,
  personalTVRoutes,
} from 'utils/routes';
import { Profile } from './Profile';
import styles from './UserDropdown.module.scss';

const labelId = 'user-dropdown-label';

function getPersonalUri(
  areenaService: Service | undefined,
  language: Language
): string {
  if (!areenaService) return `/${personalRoutes[language]}`;

  const personalUrl =
    areenaService === 'tv'
      ? personalTVRoutes[language]
      : personalPodcastRoutes[language];

  return `/${personalUrl}`;
}

type Props = {
  breakpoint: 'mobile' | 'desktop';
};

export const UserDropdown: FC<Props> = ({ breakpoint }) => {
  const t = useTranslation();
  const language = useUILanguage();
  const logOutAction = useLogOutAction();
  const { areenaService } = useAreenaService();
  const [open, setOpen] = useState(false);
  const openedByKeyboard = useRef(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-labelledby={labelId}
        className={styles.trigger}
        data-testid="user-dropdown-trigger"
        onPointerDown={() => {
          openedByKeyboard.current = false;
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            openedByKeyboard.current = true;
          }
        }}
      >
        <VisuallyHidden id={labelId}>{t('profileMenu')}</VisuallyHidden>
        <UserInitials size="sm" />
      </Popover.Trigger>

      <Popover.Content
        align={breakpoint === 'mobile' ? 'start' : 'end'}
        className={styles.content}
        side="bottom"
        sideOffset={8}
        collisionPadding={8}
        aria-labelledby={labelId}
        onOpenAutoFocus={(e) => {
          if (!openedByKeyboard.current) {
            e.preventDefault();
            return;
          }
          if (!(e.currentTarget instanceof HTMLElement)) return;
          const firstFocusable = e.currentTarget.querySelector<HTMLElement>(
            'a[href], button:not([disabled])'
          );
          if (firstFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }}
      >
        <Profile />

        <div className={styles.group}>
          <Link
            className={styles.item}
            pointer={{ uri: getPersonalUri(areenaService, language) }}
            onClick={() => setOpen(false)}
          >
            <FavoriteIcon className={styles.icon} aria-hidden />
            {t('ownPage')}
            <ChevronRightIcon className={styles.icon} aria-hidden />
          </Link>
        </div>

        <div className={styles.group}>
          <Link
            className={styles.item}
            pointer={{
              uri:
                language === 'fi'
                  ? 'https://tunnus.yle.fi/uutiskirjeet'
                  : 'https://tunnus.yle.fi/nyhetsbrev',
            }}
            onClick={() => setOpen(false)}
          >
            <MailIcon className={styles.icon} aria-hidden />
            {t('messageSettings')}
            <ChevronRightIcon className={styles.icon} aria-hidden />
          </Link>
          <Link
            className={styles.item}
            pointer={{
              uri:
                language === 'fi'
                  ? 'https://tunnus.yle.fi/historia'
                  : 'https://tunnus.yle.fi/historik',
            }}
            onClick={() => setOpen(false)}
          >
            <BarChartIcon className={styles.icon} aria-hidden />
            {t('mediaConsumption')}
            <ChevronRightIcon className={styles.icon} aria-hidden />
          </Link>
        </div>

        <div className={styles.group}>
          <button
            className={styles.item}
            data-testid="logout"
            onClick={() => {
              setOpen(false);
              logOutAction?.runAction();
            }}
          >
            {t('logOut')}
            <LogOutIcon className={styles.icon} aria-hidden />
          </button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
